import { useDispatch } from "react-redux";
import {
  getPairMeta,
  getPairTicker,
} from "@components/pages/Futures/components/Quotes/pairMeta";
import { showRiskValidationError } from "@actions/futures/positionNotifications";
import { useTranslation } from "@helpers/translate";
import {
  LONG_COLOR,
  MIN_ORDER_COST_USDT,
  MUTED_COLOR,
  SHORT_COLOR,
} from "../constants";
import { fieldWidth, formatUsd } from "../utils/format";
import { useLeverageInput } from "./useLeverageInput";
import { useRiskSettings } from "./useRiskSettings";
import { useWagerInput } from "./useWagerInput";

/**
 * Composes TradeForm UI state from Controlled props + local hooks.
 *
 * @param {object} props — same public TradeForm props from Controls
 */
export const useTradeForm = ({
  pair,
  direction,
  onDirectionChange,
  leverage,
  onLeverageChange,
  wager,
  onWagerChange,
  availableBalance,
  liquidationPrice,
  setTakeProfitPercent,
  setStopLossPercent,
  onPlaceTrade,
  isPlacingTrade = false,
}) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const isLong = direction !== "short";
  const pairMeta = getPairMeta(pair?.slug);
  const baseAsset = getPairTicker(pair?.slug);
  const directionColor = isLong ? LONG_COLOR : SHORT_COLOR;

  const wagerInput = useWagerInput({
    wager,
    onWagerChange,
    availableBalance,
  });

  const leverageInput = useLeverageInput({
    leverage,
    onLeverageChange,
  });

  const risk = useRiskSettings({
    wager,
    setTakeProfitPercent,
    setStopLossPercent,
  });

  const liquidationText = Number.isFinite(Number(liquidationPrice))
    ? formatUsd(liquidationPrice)
    : "—";

  const buildErrorText = (error) => {
    if (!error.textParams) return __(error.textKey);

    const { min, max, mode } = error.textParams;
    const fmt = (v) => (mode === "%" ? `${v}%` : `$${v}`);

    return __(error.textKey).replace("{min}", fmt(min)).replace("{max}", fmt(max));
  };

  const handlePlaceTrade = async (...args) => {
    const error = risk.getValidationError();

    if (error) {
      dispatch(
        showRiskValidationError({
          title: __(error.titleKey),
          text: buildErrorText(error),
        }),
      );
      return false;
    }

    return onPlaceTrade(...args);
  };

  return {
    isLong,
    pairMeta,
    baseAsset,
    directionColor,
    longColor: LONG_COLOR,
    shortColor: SHORT_COLOR,
    mutedColor: MUTED_COLOR,
    onDirectionChange,
    leverage,
    availableBalance,
    balanceText: formatUsd(availableBalance),
    liquidationText,
    onPlaceTrade: handlePlaceTrade,
    isPlacingTrade,
    fieldWidth,
    isWagerBelowMin: wager < MIN_ORDER_COST_USDT,
    wager: {
      ...wagerInput,
      // No error while the balance is empty or loading: there is nothing to fix in the field
      isError: wager < MIN_ORDER_COST_USDT && availableBalance > 0,
      errorText: __("futures.validation.wager_min").replace(
        "{min}",
        formatUsd(MIN_ORDER_COST_USDT),
      ),
      wagerFieldWidth: fieldWidth(wagerInput.displayWager, "1"),
    },
    leverageField: {
      ...leverageInput,
      leverageFieldWidth: fieldWidth(leverageInput.leverageDraft, "10"),
    },
    risk,
  };
};
